<script setup lang="ts">
import BigButton from "~/components/sx/components/BigButton.vue";
import {useDevelopersCode} from "~/stores/useDevelopersCode";
import TheSwagger from "~/components/TheSwagger.vue";
import {useSeoStore} from "~/stores/useSeoStore";
import {usePartnerConfig} from "~/stores/usePartnerConfig";
import {useClipboard} from '@vueuse/core';
import {CopyDocument, Check} from '@element-plus/icons-vue';

const { copied, copy } = useClipboard();
const config = useRuntimeConfig();
const { currentContent } = useDevelopersCode();
const { getDevelopersTitle, getDevelopersDescription } = useSeoStore();
const { domain } = usePartnerConfig();

const faq = [
  {
    qKey: 'pages.developers.faq.q1',
    aKey: 'pages.developers.faq.a1'
  },
  {
    qKey: 'pages.developers.faq.q2',
    aKey: 'pages.developers.faq.a2'
  },
  {
    qKey: 'pages.developers.faq.q3',
    aKey: 'pages.developers.faq.a3'
  },
  {
    qKey: 'pages.developers.faq.q4',
    aKey: 'pages.developers.faq.a4'
  },
  {
    qKey: 'pages.developers.faq.q5',
    aKey: 'pages.developers.faq.a5'
  },
];

useSeoMeta({
  ogTitle: getDevelopersTitle(),
  ogDescription: getDevelopersDescription(),
  twitterCard: 'summary_large_image',
});

useHead({
  title: getDevelopersTitle(),
  link: [
    { rel: 'canonical', href: `https://${domain.value}/developers/` }
  ],
  meta: [
    { name: 'description', content: getDevelopersDescription() }
  ],
});

definePageMeta({
  middleware: ['i18n-links'],
});
</script>

<template>
  <div class="developers">
    <section class="developers__hero">
      <div class="developers__desc">
        <h1 class="sx-landing-title">
          {{ $t('pages.developers.title') }}
        </h1>
        <p class="developers__subtitle sx-landing-subtitle">
          {{ $t('pages.developers.subtitle') }}
        </p>
      </div>
      <BigButton
        :text="$t('pages.developers.tryFree')"
        :to="config.public.signinUrl"
        style="min-width: 249px; margin-bottom: 40px"
      />
      <div class="developers__img-wrapper">
        <img src="~/assets/images/sx/computer.png" alt="computer" loading="lazy">
      </div>
    </section>

    <section class="developers__section developers__code">
      <div class="developers__desc">
        <h2 class="sx-landing-title">
          {{ $t('pages.developers.howWork') }}
        </h2>
        <p class="developers__subtitle sx-landing-subtitle">
          {{ $t('pages.developers.howWorkSubtitle') }}
        </p>
      </div>
      <CodeLangSwitch />
      <div style="overflow: hidden; margin-top: 48px; border-radius: 24px">
        <div class="developers__code-content">
          <div class="developers__code-icon">
            <el-icon size="16">
              <CopyDocument
                v-if="!copied"
                style="cursor: pointer"
                @click="copy(currentContent)"
              />
              <Check v-else />
            </el-icon>
          </div>
          <pre>{{ currentContent }}</pre>
        </div>
      </div>
    </section>

    <section class="developers__section">
      <div class="developers__desc">
        <h2 class="sx-landing-title">
          {{ $t('pages.developers.moreOptions') }}
        </h2>
        <p class="developers__subtitle sx-landing-subtitle">
          {{ $t('pages.developers.moreOptionsSubtitle') }}
        </p>
      </div>
      <client-only>
        <TheSwagger />
      </client-only>
    </section>

    <section style="width: 100%">
      <sx-proxy-faq :type="'resident'" :faq="faq" />
    </section>
  </div>
</template>

<style scoped lang="scss">
.developers {
  font-family: AlsHauss, sans-serif;
  display: flex;
  flex-direction: column;
  text-align: center;
  justify-content: center;
  max-width: 1264px;
  width: 100%;
  margin: 0 auto;
  padding: 0 32px;
  align-items: center;

  &__section {
    padding: 88px 0;
    width: 100%;
  }

  &__hero {
    margin-bottom: 39px;
    width: 100%;
    @media (max-width: 600px) {
      margin-bottom: 0;
      img {
        width: 100%;
      }
    }
  }

  &__desc {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 32px;
    align-items: center;
  }

  &__title {
    max-width: 920px;
  }

  &__subtitle {
    max-width: 750px;
  }

  &__img-wrapper {
    max-width: 1024px;
    margin:  0 auto;
    display: flex;
    justify-content: center;
  }

  &__code {
    @media (max-width: 600px) {
      padding-top: 1rem;
    }
    &-content {
      position: relative;
      padding: 32px;
      max-height: 600px;
      overflow: auto;
      background: #EFF0F3;
      border-radius: 24px;
      color: #666666;
      font-size: 14px;
      font-weight: 400;
      line-height: 24px;
      text-align: left;
      @media (max-width: 600px) {
        padding: 1rem;
      }
      pre {
        text-wrap: wrap;
      }
    }
    &-icon {
      position: absolute;
      top: 1rem;
      right: 1rem;
      &:hover {
        svg {
          :deep(path) {
            transition: .3s;
            fill: #000;
          }
        }
      }
    }
  }
}
:deep(.swagger-ui .scheme-container .schemes:not(:has(.schemes-server-container))) {
  justify-content: center !important;
}
:deep(.wrapper) {
  text-align: start !important;
}
</style>
